import type { SYSFormValidationValidators } from "../.."
import { ovl } from "../.."

import {
  /*HookFormDirtyChanged,*/
  HookFormValidatorsCustom,
  HookFormValidatorsCustom_Type,
} from "../../global/hooks"

import { FieldSetFocus, SYSField, SYSFields, SYSFormState } from "./fieldapi"
import { validatorsFunctions } from "./helper"
import { ValidationsAdd } from "./validatorsapi"

export type ValidationSummaryGroup = "Field" | "ValidationKey"

export type ValidationContextType = {
  mode: "System" | "Validate" | "ValueChanged"
}

export type ValidationResetContext = "System" | "Validate" | "ValueChanged"

export type OptionsValidate = {
  displayType?: FormValidationDisplayType
  summaryGroup?: ValidationSummaryGroup
  summaryDisplayType?: FormValidationSummaryDisplayType
}

export type OptionsValidateResultError = OptionsValidate & {
  translationReps?: any[]
}

export type FieldValidationResult = {
  System: ValidateResultError[]
  ValueChanged: ValidateResultError[]
  Validate: ValidateResultError[]
}

export type ValidateResultError = {
  translationKey?: string
  translationReps?: any[]
  displayType?: FormValidationDisplayType
  resetContext?: ValidationResetContext
  summaryGroup?: ValidationSummaryGroup
  summaryDisplayType?: FormValidationSummaryDisplayType
  // visible will be filled by the setValidate Method and tells ovl that this validation should be displayed

  handledFieldKeys?: string[]
  validationKey?: string
}

export type ValidateResultVisibleSummaryError = {
  translationKey: string
  translationReps?: string[]
}

export type ValidateResultSummaryError = {
  translationKey: string
  translationReps?: string[]
  displayType: FormValidationSummaryDisplayType
  standalone?: boolean
}
export type ValidateResult = {
  // its just an array
  errors: ValidateResultError[]
}

export type ValidateSummaryResult = {
  // its just an array
  visibleErrors: ValidateResultVisibleSummaryError[]
  errors: ValidateResultSummaryError[]
}

export type ValidationsAddOptions = {
  validation?: OptionsValidateResultError
  fields?: SYSField | SYSField[]
  setFocus?: boolean
  resetContext?: ValidationResetContext
  dependents?: SYSField[]
}

export type ValidatorOptions = {
  validation?: OptionsValidateResultError
  // val could be like 10 for an ExactLength === 10 Validation. or {min:8,max:10} for an Range Validation ...
  // its really multi purpose and should be checked in the validator
  val?: any
  setFocus?: boolean
  resetContext?: ValidationResetContext
  dependents?: SYSField[]
}

export type ValidatorUseOptions = ValidatorOptions & {
  field?: SYSField
}

export type FormValidationDisplayType = "WhenTouched" | "Always" | "OnlyOutline"
export type FormValidationSummaryDisplayType =
  | "WhenTouched"
  | "WhenAllRelatedFieldsTouched"
  | "Always"
  | "None"

export const _validationsAdd = (
  fields: SYSField | SYSField[],
  translationKey: string,
  resetContext: ValidationResetContext,
  options?: ValidatorOptions
) => {
  ValidationsAdd(fields, translationKey, resetContext, options)
}
export const _validatorUse = (
  field: SYSField,
  validator: SYSFormValidationValidators,
  resetContext: ValidationResetContext,
  options?: ValidatorOptions
): boolean => {
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  if (!formState) {
    return
  }
  if (!options) {
    options = { val: undefined }
  }
  let validation = options?.validation
  let fieldDefaultValidationOptions: OptionsValidateResultError =
    field.schema.defaultValidationOptions
  validation = _enrichValidation(
    formState,
    validation,
    `SYSValidator${validator}`,
    resetContext,
    undefined,
    fieldDefaultValidationOptions
  )
  options.validation = validation
  let res = _callValidator(validator, field, options)
  let fields = [field]
  if (res) {
    _addValidation(fields, validation, options.setFocus, options.dependents)
  }
  return !res
}

export const _callValidator = (
  validator: SYSFormValidationValidators,
  field: SYSField,
  options: ValidatorOptions
) => {
  let res
  if (options.validation.translationReps)
    if (validatorsFunctions[validator].type === "SYS") {
      res = validatorsFunctions[validator].fn(field, options)
    } else if (ovl.getActions().app.global.hooks?.[HookFormValidatorsCustom]) {
      res = ovl.getActions().app.global.hooks?.[HookFormValidatorsCustom]({
        field,
        options,
        validator,
      } as HookFormValidatorsCustom_Type)
    } else {
      throw new Error(`Ovl: hook ${HookFormValidatorsCustom} not found`)
    }
  return res
}

export const _enrichValidation = (
  formState: SYSFormState,
  validation: ValidateResultError,
  translationKey: string,
  resetContext: ValidationResetContext,
  group?: any,
  fieldDefaultValidationOptions?: OptionsValidateResultError
): ValidateResultError => {
  if (validation === undefined) {
    validation = {
      displayType: fieldDefaultValidationOptions?.displayType
        ? fieldDefaultValidationOptions.displayType
        : formState.validationDefault.displayType,
      translationKey,
      translationReps: [],
    }
  }
  validation.translationKey = translationKey
  validation.validationKey = translationKey
  if (group !== undefined) {
    validation.validationKey = `validation.validationKey${group}`
  }

  // set the defaults if necessary
  if (!validation.summaryDisplayType) {
    if (fieldDefaultValidationOptions?.summaryDisplayType) {
      validation.summaryDisplayType =
        fieldDefaultValidationOptions.summaryDisplayType
    } else if (formState.validationDefault.summaryDisplayType) {
      validation.summaryDisplayType =
        formState.validationDefault.summaryDisplayType
    }
  }

  // for summary display either summaryGroup or summaryDisplayType (or both) needs to be set
  // create the defaults if one is missing
  //<summary defaults>
  if (!validation.summaryGroup) {
    if (fieldDefaultValidationOptions?.summaryGroup) {
      validation.summaryGroup = fieldDefaultValidationOptions.summaryGroup
    } else if (formState.validationDefault.summaryGroup) {
      validation.summaryGroup = formState.validationDefault.summaryGroup
    } else if (validation.summaryDisplayType) {
      validation.summaryGroup = "ValidationKey"
    }
  }
  if (!validation.summaryDisplayType && validation.summaryGroup) {
    // if (formState.validationDefault.summaryDisplayType) {
    //   validation.summaryDisplayType =
    //     formState.validationDefault.summaryDisplayType
    // } else if (validation.summaryGroup) {
    validation.summaryDisplayType = "WhenTouched"
    //}
  }
  //</summary defaults>

  if (!validation.displayType) {
    if (fieldDefaultValidationOptions?.displayType) {
      validation.displayType = fieldDefaultValidationOptions.displayType
    } else if (validation.summaryDisplayType) {
      validation.displayType = "OnlyOutline"
    } else {
      validation.displayType = "WhenTouched"
    }
  }
  if (!validation.translationReps) {
    validation.translationReps = []
  }

  if (resetContext !== undefined) {
    validation.resetContext = resetContext
  }
  if (validation.resetContext === undefined) {
    validation.resetContext = "Validate"
  }

  return validation
}

export const ValidationsRemove = (
  fields: SYSField | SYSField[],
  validationkey?: string
) => {
  if (!Array.isArray(fields)) {
    fields = [fields]
  }
  _removeValidation(fields, validationkey)
}

const _removeValidation = (fields: SYSField[], validationkey?: string) => {
  fields.forEach((field) => {
    // if validatiuonkey is undefine just remove all field validations
    let needsSetVisibleValidation = false
    let formsState = ovl.getState().ovl.forms
    var res = field.validationResult.System.filter(
      (f) => validationkey !== undefined && f.validationKey !== validationkey
    )
    if (res.length !== field.validationResult.System.length) {
      field.validationResult.System = res
      needsSetVisibleValidation = true
    }

    res = field.validationResult.Validate.filter(
      (f) => validationkey !== undefined && f.validationKey !== validationkey
    )
    if (res.length !== field.validationResult.Validate.length) {
      field.validationResult.Validate = res
      needsSetVisibleValidation = true
    }

    res = field.validationResult.ValueChanged.filter(
      (f) => validationkey !== undefined && f.validationKey !== validationkey
    )
    if (res.length !== field.validationResult.ValueChanged.length) {
      field.validationResult.ValueChanged = res
      needsSetVisibleValidation = true
    }
    if (
      needsSetVisibleValidation &&
      (field.ui.alwaysReadOnly ||
        (<SYSFormState>formsState[field.formType][field.formId])._state
          ._inInitOrChangedCount === 0)
    ) {
      // only do immediate summary visible and form valid if other form than initiating form
      setVisibleValidation(formsState[field.formType][field.formId])
      _setFormValid(formsState[field.formType][field.formId])
    }
  })
}

export const _addValidation = (
  fields: SYSField[],
  v: ValidateResultError,

  setFocus?: boolean,
  dependents?: SYSField[]
) => {
  v.handledFieldKeys = fields.map((m) => m.fieldKey)
  if (dependents) {
    v.handledFieldKeys = v.handledFieldKeys.concat(
      dependents.map((m) => m.fieldKey)
    )
  }
  let formState: SYSFormState
  try {
    fields.forEach((field) => {
      if (!formState) {
        formState = ovl.getState().ovl.forms[field.formType][field.formId]
      }
      // if we set mandatory in valuechanged and there are already System Errors delete the System Error
      // UseCase: A Field accepts onl List Entries -> This gets Set in the System Group
      // A User adds a Mandatory afterwards (Be it Changed or Validate Hook) then it displays 2 Error whereas Mandatory only should be displayed

      // <250918 removed because testcase frm valuechanged SchemaMandatory group was not working
      // if (
      //   v.resetContext !== "System" &&
      //   v.translationKey === "SYSValidatorMandatory"
      // ) {
      //   field.validationResult.System = []
      // }
      ///>

      // do nothing if key is already added
      let allValidation = field.validationResult.System.concat(
        field.validationResult.Validate
      ).concat(field.validationResult.ValueChanged)
      if (
        allValidation.filter(
          (f) =>
            (f.translationKey === v.translationKey &&
              f.summaryGroup === v.summaryGroup &&
              f.resetContext === v.resetContext) ||
            f.validationKey === v.validationKey
        ).length < 1
      ) {
        formState.valid = false
        if (setFocus && v.displayType !== "OnlyOutline") {
          v.displayType = "Always"
        }

        let translationReps = []

        translationReps.push(field.ui.labelCaption)

        if (v.translationReps) {
          translationReps = translationReps.concat(
            v.translationReps.filter((f) => f)
          )
        }
        v.translationReps = translationReps
        field.validationResult[v.resetContext].push(v)
      }
    })

    if (setFocus) {
      setTimeout(() => {
        FieldSetFocus(fields[0])
      }, 400)
    }
  } finally {
    // do the debounced one
    setVisibleValidation(formState)
  }
}

type ValidateResultErrorWithFieldWatched = ValidateResultError & {
  watched?: boolean
  fieldKey?: string
}

export const setVisibleValidation = (
  formState: SYSFormState,
  debounce: number = 300
) => {
  if (!formState) {
    return
  }
  // if (ValidationContext.mode === undefined) {
  //   // use debounced if called from outside ovl base context
  //   // user might use a lot of subsequent validations from whatever event. so only the last one should be relevant
  if (formState._state._summaryDebouncer) {
    clearTimeout(formState._state._summaryDebouncer)
  }
  if (debounce < 1) {
    _setVisibleValidation(formState)
    return
  }
  formState._state._summaryDebouncer = setTimeout(() => {
    _setVisibleValidation(formState)
  }, debounce)
}

type SummaryFirstOrAllWatchedType = {
  [key in string]: {
    fieldKey: string
    watched: boolean
    translationReps: any[]
    summaryGroup: ValidationSummaryGroup
  }[]
}

export const _setFieldValid = (field: SYSField) => {
  if (field === undefined || !field._state.fieldInitialValidated) {
    return
  }

  if (field.inactive && !field.inactiveButRelevantForValidation) {
    field.valid = true
  }

  let allValidation = []

  allValidation = allValidation.concat(field.validationResult.System)

  allValidation = allValidation.concat(field.validationResult.Validate)

  allValidation = allValidation.concat(field.validationResult.ValueChanged)

  field.valid = allValidation.length === 0
}

const _setVisibleValidation = (formState: SYSFormState) => {
  // for some reason there were console errors indicating that formstate was not anmore there
  // (its possible because timeout calling stuff)
  // so just leave if its not anymore there
  if (!formState) {
    return
  }

  let fields: SYSFields = formState.fields
  let summaryFieldKeysAllWatched: SummaryFirstOrAllWatchedType = {}

  // delete the field validation results
  Object.keys(formState.validationResult).forEach((k) => {
    formState.validationResult[k].fieldErrors = []
    //formState.validationResult[k].summaryErrors = {}
    formState.validationResult[k].validationKeyErrors = {}
  })

  Object.keys(fields)
    .filter(
      (f) => !fields[f].inactive || fields[f].inactiveButRelevantForValidation
    )
    .forEach((fieldKey) => {
      let allValidation = fields[fieldKey].validationResult.System.concat(
        fields[fieldKey].validationResult.Validate
      ).concat(fields[fieldKey].validationResult.ValueChanged)

      allValidation.forEach((r) => {
        let tabErrors = formState.validationResult[fields[fieldKey].groups.tab]
        let rr: ValidateResultErrorWithFieldWatched = r
        rr.watched = fields[fieldKey].watched
        rr.fieldKey = fieldKey
        if (rr.summaryGroup) {
          if (rr.summaryDisplayType === "WhenAllRelatedFieldsTouched") {
            if (!summaryFieldKeysAllWatched[rr.translationKey]) {
              summaryFieldKeysAllWatched[rr.translationKey] = []
            }
            summaryFieldKeysAllWatched[rr.translationKey].push({
              fieldKey,
              watched: rr.watched,
              translationReps: rr.translationReps,

              summaryGroup: rr.summaryGroup,
            })
          }

          if (
            rr.summaryDisplayType === "Always" ||
            (rr.summaryDisplayType === "WhenTouched" &&
              fields[fieldKey].watched)
          ) {
            if (rr.summaryGroup === "Field") {
              let fieldErrors = tabErrors.fieldErrors
              fieldErrors.push({
                translationKey: rr.translationKey,
                translationReps: rr.translationReps,
                involvedFieldKeys: [fieldKey],
              })
            } else {
              if (!tabErrors.validationKeyErrors[rr.validationKey]) {
                tabErrors.validationKeyErrors[rr.validationKey] = {
                  translationKey: rr.translationKey,
                  translationReps: rr.translationReps,
                  involvedFieldKeys: [rr.fieldKey],
                  displayAlways: rr.summaryDisplayType === "Always",
                }
              } else {
                let involvedFieldKeys =
                  tabErrors.validationKeyErrors[rr.validationKey]
                    .involvedFieldKeys

                involvedFieldKeys.push(rr.fieldKey)
              }
            }
          }
        }
      })
    })

  // now the allWatched
  Object.keys(summaryFieldKeysAllWatched).forEach((f) => {
    let allWatchedPerKey = summaryFieldKeysAllWatched[f].filter(
      (f) => f.summaryGroup === "ValidationKey"
    )
    let watchedLength = allWatchedPerKey.filter((f) => f.watched).length
    if (watchedLength > 0 && allWatchedPerKey.length === watchedLength) {
      allWatchedPerKey.forEach((allWatchedEntry) => {
        let tabErrors =
          formState.validationResult[
            fields[allWatchedEntry.fieldKey].groups.tab
          ]
        if (!tabErrors.validationKeyErrors[f]) {
          tabErrors.validationKeyErrors[f] = {
            translationKey: f,
            translationReps: allWatchedEntry.translationReps,
            involvedFieldKeys: allWatchedPerKey.map((m) => m.fieldKey),
            displayAlways: false,
          }
        } else {
          let involvedFieldKeys =
            tabErrors.validationKeyErrors[f].involvedFieldKeys
          if (involvedFieldKeys.indexOf(allWatchedEntry.fieldKey) < 0) {
            involvedFieldKeys.push(allWatchedEntry.fieldKey)
          }
        }
      })
    }

    allWatchedPerKey = summaryFieldKeysAllWatched[f].filter(
      (f) => f.summaryGroup === "Field"
    )
    watchedLength = allWatchedPerKey.filter((f) => f.watched).length
    if (watchedLength > 0 && allWatchedPerKey.length === watchedLength) {
      allWatchedPerKey.forEach((allWatchedEntry) => {
        let tabErrors =
          formState.validationResult[
            fields[allWatchedEntry.fieldKey].groups.tab
          ]
        if (tabErrors.fieldErrors[f] === undefined) {
          tabErrors.fieldErrors[f] = []
        }
        tabErrors.fieldErrors.push({
          involvedFieldKeys: [allWatchedEntry.fieldKey],
          translationKey: f,
          translationReps: allWatchedEntry.translationReps,
          displayAlways: false,
        })
      })
    }
  })
}

export const _checkFormValid = (formState: SYSFormState, tab: number = -1) => {
  let tabS = tab.toString()
  let fields = formState.fields
  let validationResults = Object.keys(formState.validationResult).filter(
    (f) => tab === -1 || f === tabS
  )
  return (
    !validationResults.some(
      (f) => Object.keys(formState.validationResult[f].summaryErrors).length > 0
    ) &&
    !Object.keys(fields)
      .filter(
        (f) =>
          !fields[f].schema.isCalculatedColumn &&
          (!fields[f].inactive || fields[f].inactiveButRelevantForValidation) &&
          (tab === -1 || fields[f].groups.tab === tab)
      )
      .some(
        (s) =>
          fields[s].validationResult.System.length +
            fields[s].validationResult.Validate.length +
            fields[s].validationResult.ValueChanged.length >
          0
      )
  )
}

export const _setFormValid = (formState: SYSFormState): boolean => {
  if (!formState) {
    return false
  }
  formState.valid = _checkFormValid(formState)
}

export const _getFormValid = (formState: SYSFormState): boolean => {
  if (!formState) {
    return false
  }
  if (formState._state._inInitOrChangedCount > 0) {
    // we are possibly inside hooks in init or validate or changed
    // they assume the errors list to be correctly at that stage. so call the full shabang
    return _checkFormValid(formState)
  }
  return formState.valid
}

// export const _clearAllErrors = (formState: SYSFormState) => {
//   Object.keys(formState.validationResult.errors = []
//   formState.validationResult.visibleErrors = []
//   let fields = formState.fields
//   Object.keys(fields).forEach((f) => {
//     fields[f].validationResult.Validate = []
//     fields[f].validationResult.System = []
//     fields[f].validationResult.ValueChanged = []
//   })
//   setVisibleValidation(formState, 0)
//   //_setFormValid(formState)
// }

// export const setFormDirty = (formState: SYSFormState) => {
//   let newDirty = Object.keys(formState.fields).some(
//     (s) =>
//       formState.fields[s].dirty &&
//       !formState.fields[s].schema.isCalculatedColumn // &&
//     // (!formState.fields[s].inactive ||
//     //   formState.fields[s].inactiveButRelevantForValidation)
//   )

//   if (newDirty !== formState.dirty) {
//     if (newDirty) {
//       ovl.getActions().ovl.internal._SetFormDirty(formState)
//     } else {
//       ovl.getActions().ovl.internal._SetFormUndirty(formState)
//     }

//     let fn = ovl.getActions().app.features[formState.feature]

//     let hookFormDirtyChanged =
//       fn?.hooks?.[
//         HookFormDirtyChanged.replace("{subFeature}", formState.subFeature)
//       ]
//     if (hookFormDirtyChanged) {
//       hookFormDirtyChanged({ formState, newDirty })
//     }
//   }
// }

// export const SetFormUnDirty = (formState: SYSFormState) => {
//   ovl.getActions().ovl.internal._SetFormUndirty(formState)
// }

export const HasVisibleSummaryErrors = (formState: SYSFormState) => {
  return Object.keys(formState.validationResult).some(
    (s) =>
      formState.validationResult[s].fieldErrors.length > 0 ||
      Object.keys(formState.validationResult[s].summaryErrors).length > 0 ||
      Object.keys(formState.validationResult[s].validationKeyErrors).length > 0
  )
}

// export const HasVisibleSummaryErrors = (formState: SYSFormState) => {
//   return Object.keys(formState.validationResult).some(
//     (s) =>
//       Object.keys(formState.validationResult[s].summaryErrors).length > 0 ||
//       Object.keys(formState.fields).some((s) => {
//         return (
//           formState.fields[s].validationResult.System.some(
//             (s) => s.summaryGroup
//           ) ||
//           formState.fields[s].validationResult.Validate.some(
//             (s) => s.summaryGroup
//           ) ||
//           formState.fields[s].validationResult.ValueChanged.some(
//             (s) => s.summaryGroup
//           )
//         )
//       })
//   )
// }
